import { z } from "zod";

export const PageableRequestSchema = z.object({
  page: z.number().optional().nullable(),
  size: z.number().optional().nullable(),
  sort: z.array(z.string()).optional(),
});

export type PageableRequest = z.infer<typeof PageableRequestSchema>;

export const PageableSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    totalPages: z.number(),
    totalElements: z.number(),
    pageSize: z.number(),
    next: z.string().nullable().optional(),
    previous: z.string().nullable().optional(),
  });

export const CompanyInfoResponseSchema = z.object({
  id: z.number(),
  companyAdminId: z.number(),
  companyName: z.string(),
  ceoName: z.string().nullable().optional(),
  logoUrl: z.string().nullable().optional(),
  faviconUrl: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
  /** 쇼핑몰 검색 노출 활성화 여부. 구버전 백엔드 응답에는 없을 수 있다(그 경우 허용으로 본다) */
  searchEngineIndexingEnabled: z.boolean().nullable().optional(),
  phone: z.string().nullable().optional(),
  secondaryContactType: z.string().nullable().optional(),
  secondaryContact: z.string().nullable().optional(),
  companySealImageUrl: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  addressDetail: z.string().nullable().optional(),
  ecommerceBizNumber: z.string().nullable().optional(),
  businessNumber: z.string().nullable().optional(),
  businessType: z.string().nullable().optional(),
  businessCategory: z.string().nullable().optional(),
  businessRegistrationFileUrl: z.string().nullable().optional(),
  /** 쇼핑몰 푸터에 한 줄 더 노출하는 추가 사업자 정보 (표시 전용 자유 텍스트). 줄바꿈으로 여러 줄 가능 */
  additionalBusinessInfo: z.string().nullable().optional(),
  homeTaxKey: z.string().nullable().optional(),
  tossPayKey: z.string().nullable().optional(),
  hasTossSecretKey: z.boolean().optional(),
  channelTalkKey: z.string().nullable().optional(),
  businessHours: z.string().nullable().optional(),
  lunchBreak: z.string().nullable().optional(),
  businessHoursNotice: z.string().nullable().optional(),
  shopId: z.number().nullable().optional(),
  disclosureText: z.string().nullable().optional(),
  /** 사이트 운영 종료 예정일. 예약된 회사만 값이 있다. 헤더 안내 줄에 쓴다. */
  closingScheduledAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
});

export type CompanyInfoResponse = z.infer<typeof CompanyInfoResponseSchema>;

export const UserInfoResponseSchema = z.object({
  id: z.number(),
  companyId: z.number().nullable().optional(),
  email: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  mobile: z.string().nullable().optional(),
  createdAt: z.string(),
  role: z.enum(["ADMIN", "COMPANY_ADMIN", "COMPANY_MANAGER", "USER"]),
  socialAuth: z.array(z.enum(["KAKAO", "NAVER", "GOOGLE"])),
  deletedAt: z.string().nullable().optional(),
  departmentId: z.number().nullable().optional(),
  departmentName: z.string().nullable().optional(),
});

export type UserInfoResponse = z.infer<typeof UserInfoResponseSchema>;

/**
 * 업체 등록 신청서. 세금계산서/현금영수증 발급(국세청 인증 연동사 회원가입) 필수 항목을 신청 단계에서 받는다.
 * 승인되면 회사 정보로 옮겨진다.
 */
export const CompanyApplicationSchema = z.object({
  companyName: z.string(),
  ceoName: z.string(),
  businessNumber: z.string(),
  businessType: z.string(),
  businessCategory: z.string(),
  zipCode: z.string().nullable().optional(),
  address: z.string(),
  addressDetail: z.string().nullable().optional(),
  phone: z.string(),
});

export type CompanyApplication = z.infer<typeof CompanyApplicationSchema>;

export const LandingUserInfoResponseSchema = z.object({
  id: z.number(),
  companyId: z.number().nullable().optional(),
  email: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  mobile: z.string().nullable().optional(),
  createdAt: z.string(),
  role: z.enum(["ADMIN", "COMPANY_ADMIN", "COMPANY_MANAGER", "USER"]),
  socialAuth: z.array(z.enum(["KAKAO", "NAVER", "GOOGLE"])),
  deletedAt: z.string().nullable().optional(),
  isRequestCompany: z.boolean(),
  requestedCompanyName: z.string().nullable().optional(),
  emailVerified: z.boolean().optional().default(true),
  /** 관리자 신청 목록에서만 채워진다. 예전 신청(업체명만)은 null */
  companyRequest: CompanyApplicationSchema.extend({ userId: z.number() }).nullable().optional(),
});

export type LandingUserInfoResponse = z.infer<
  typeof LandingUserInfoResponseSchema
>;
